import * as THREE from 'three';

/**
 * ===========================================================================
 *  VFX SPRITE ATLAS — every particle texture in the game, drawn by maths.
 * ===========================================================================
 *
 * One 1024x1024 RGBA texture, 4x4 cells of 256px. RGB is always white; the
 * shape lives entirely in the alpha channel so a single sprite can be tinted
 * to any element colour by the per-particle vertex colour.
 *
 * Cell indices are the `sprite` field of every effect definition:
 *
 *   0 DOT      soft round falloff            smoke, dust, haze bodies
 *   1 GLOW     tight core + wide halo        sparks, motes, embers
 *   2 STREAK   horizontal taper, hot spine   radial hit streaks, speed lines
 *   3 STAR     4-point flare + core          the hit-spark centre
 *   4 PUFF     fbm-broken cloud              footfall dust
 *   5 PUFF2    wispier cloud, other seed     landing dust, slides
 *   6 RING     soft annulus                  expanding impact ring
 *   7 SLASH    tapered crescent arc          the swept slash card
 *   8 FLAME    teardrop with licking edges   fire element
 *   9 SHARD    hexagonal crystal             ice element
 *  10 BOLT     jagged vertical arc           lightning element
 *  11 EMBER    bright head + short tail      cinders, musou motes
 *  12 WISP     long streaky smoke            drifting battlefield haze
 *  13 RAYS     radial sunburst of thin rays  musou light shafts, crit flare
 *  14 DEBRIS   solid irregular chunk         dirt clods, splinters
 *  15 SMOKE    very soft low-contrast blob   the persistent low haze
 *
 * Nothing here runs after boot.
 */

export const SPR = {
  DOT: 0, GLOW: 1, STREAK: 2, STAR: 3, PUFF: 4, PUFF2: 5, RING: 6, SLASH: 7,
  FLAME: 8, SHARD: 9, BOLT: 10, EMBER: 11, WISP: 12, RAYS: 13, DEBRIS: 14, SMOKE: 15,
};

export const ATLAS_GRID = 4;

/* ------------------------------------------------------------ tiny noise -- */
function hash2(x, y, s) {
  let h = Math.imul(x | 0, 374761393) ^ Math.imul(y | 0, 668265263) ^ Math.imul(s | 0, 2246822519);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}
function vn(x, y, s) {
  const xi = Math.floor(x), yi = Math.floor(y);
  const fx = x - xi, fy = y - yi;
  const ux = fx * fx * (3 - 2 * fx), uy = fy * fy * (3 - 2 * fy);
  const a = hash2(xi, yi, s), b = hash2(xi + 1, yi, s);
  const c = hash2(xi, yi + 1, s), d = hash2(xi + 1, yi + 1, s);
  const ab = a + (b - a) * ux, cd = c + (d - c) * ux;
  return ab + (cd - ab) * uy;
}
function fbm(x, y, s, oct = 4) {
  let a = 1, f = 1, sum = 0, n = 0;
  for (let i = 0; i < oct; i++) { sum += a * vn(x * f, y * f, s + i * 977); n += a; a *= 0.5; f *= 2.03; }
  return sum / n;
}
const sat = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const sstep = (e0, e1, v) => { const t = sat((v - e0) / (e1 - e0 || 1e-6)); return t * t * (3 - 2 * t); };

/* --------------------------------------------------------------- shapes --- */
/**
 * Each shape takes normalised coordinates u,v in [-1,1] (v points UP) and
 * returns coverage in [0,1]. They are deliberately written as continuous
 * functions rather than canvas gradients so the falloffs are exact and the
 * result is byte-identical on every machine.
 */
const SHAPES = [
  /* 0 DOT */ (u, v, r) => Math.exp(-3.1 * r * r) * sstep(1.0, 0.72, r),
  /* 1 GLOW */ (u, v, r) => sat(Math.exp(-19 * r * r) * 1.0 + Math.exp(-3.4 * r * r) * 0.34) * sstep(1.0, 0.80, r),
  /* 2 STREAK — long on +v, because align modes 1/3 stretch the quad's Y axis */
  (u, v) => {
    const body = Math.exp(-30 * u * u) * Math.pow(sat(1 - Math.abs(v)), 1.4);
    const core = Math.exp(-260 * u * u) * Math.pow(sat(1 - Math.abs(v)), 0.55);
    const head = Math.exp(-((u * u) / 0.010 + ((v - 0.72) ** 2) / 0.020)) * 0.85;
    return sat(body * 0.55 + core * 0.85 + head);
  },
  /* 3 STAR */ (u, v, r) => {
    // A hit spark is a small white core with hard lens spikes. A soft ball of
    // light plus this engine's bloom just makes fog, so the core is tight and
    // the spikes are thin and long.
    const spikeH = Math.exp(-1400 * v * v) * Math.pow(sat(1 - Math.abs(u)), 1.15);
    const spikeV = Math.exp(-1400 * u * u) * Math.pow(sat(1 - Math.abs(v)), 1.15);
    const d1 = (u + v) * 0.7071, d2 = (u - v) * 0.7071;
    const diag = (Math.exp(-3600 * d1 * d1) + Math.exp(-3600 * d2 * d2)) * Math.pow(sat(1 - r), 1.6) * 0.42;
    const core = Math.exp(-120 * r * r);
    const halo = Math.exp(-11.0 * r * r) * 0.12;
    return sat(core + halo + spikeH * 0.95 + spikeV * 0.95 + diag) * sstep(1.0, 0.92, r);
  },
  /* 4 PUFF */ (u, v, r) => {
    const n = fbm(u * 2.4 + 5, v * 2.4 + 5, 11, 4) - 0.5;
    const bumps = fbm(u * 5.5 + 2, v * 5.5 + 2, 71, 3) - 0.5;
    return sat(sstep(1.0, 0.18, r + n * 0.52 + bumps * 0.18)) * 0.96;
  },
  /* 5 PUFF2 */ (u, v, r) => {
    const n = fbm(u * 1.9 - 3, v * 2.7 - 3, 313, 4) - 0.5;
    const bumps = fbm(u * 6.8, v * 6.8, 907, 3) - 0.5;
    return sat(sstep(1.0, 0.12, r + n * 0.66 + bumps * 0.20)) * 0.92;
  },
  /* 6 RING */ (u, v, r) => {
    const d = r - 0.70;
    const band = Math.exp(-(d * d) / 0.0068);
    const inner = sstep(0.70, 0.34, r) * 0.05;
    const rim = Math.exp(-((r - 0.775) * (r - 0.775)) / 0.0012) * 0.40;
    return sat(band * 0.62 + inner + rim) * sstep(1.0, 0.94, r);
  },
  /* 7 SLASH */ (u, v, r) => {
    // A crescent swept about the origin: thick and hot at the centre of the
    // arc, drawn to nothing at both ends. This is the card a single sweep
    // leaves behind when there is no skeleton to sample.
    const a = Math.atan2(v, u);
    const half = 1.15;                       // +-66 degrees of arc
    const ta = sat(1 - Math.abs(a) / half);
    if (ta <= 0) return 0;
    const w = 0.030 + 0.085 * Math.pow(ta, 0.65);
    const d = (r - 0.70) / w;
    const body = Math.exp(-d * d) * Math.pow(ta, 0.9) * 0.72;
    const edge = Math.exp(-(((r - 0.755) / (w * 0.26)) ** 2)) * Math.pow(ta, 0.55) * 1.0;
    return sat(body + edge);
  },
  /* 8 FLAME */ (u, v, r) => {
    const y = (v + 1) * 0.5;                                   // 0 bottom .. 1 top
    const wob = (fbm(u * 3.0, v * 2.2 - 1.4, 501, 4) - 0.5) * 0.42 * y;
    const w = 0.52 * Math.pow(sat(1 - y), 0.55) * (0.55 + 0.75 * sstep(0.0, 0.28, y));
    const d = Math.abs(u - wob) / Math.max(0.03, w);
    const tongue = Math.exp(-d * d * 2.1) * sstep(1.02, 0.86, y) * sstep(-1.02, -0.80, v);
    const core = Math.exp(-(u * u) / 0.028) * sstep(0.9, 0.2, y) * 0.55;
    return sat(tongue + core);
  },
  /* 9 SHARD */ (u, v, r) => {
    // hexagonal prism seen flat-on, bright rim, translucent middle
    const a = Math.atan2(v, u);
    const k = Math.PI / 3;
    const ha = a - Math.round(a / k) * k;
    const hex = r * Math.cos(ha) / 0.80;
    const body = sstep(1.0, 0.86, hex) * 0.42;
    const rim = Math.exp(-(((hex - 0.92) / 0.09) ** 2)) * 0.95;
    const spine = Math.exp(-(u * u) / 0.006) * sstep(1.0, 0.8, hex) * 0.35;
    return sat(body + rim + spine);
  },
  /* 10 BOLT */ (u, v) => {
    const y = (v + 1) * 0.5;
    const j = (fbm(v * 3.4 + 9, 0.5, 1301, 3) - 0.5) * 0.9 + (fbm(v * 9.0, 2.5, 733, 2) - 0.5) * 0.34;
    const d = Math.abs(u - j);
    const core = Math.exp(-(d * d) / 0.0018);
    const glow = Math.exp(-(d * d) / 0.045) * 0.42;
    const branch = Math.exp(-((Math.abs(u - j * 2.2) - 0.22) ** 2) / 0.004) * sstep(0.62, 0.42, y) * 0.35;
    return sat((core + glow + branch) * sstep(1.0, 0.90, Math.abs(v)) * sstep(-1.0, -0.9, -Math.abs(v) * 0 + 1));
  },
  /* 11 EMBER */ (u, v, r) => {
    const head = Math.exp(-((u * u) / 0.010 + ((v - 0.30) ** 2) / 0.010));
    const tail = Math.exp(-(u * u) / 0.014) * sstep(0.34, -0.72, v) * sstep(-0.95, -0.6, v) * 0.55;
    const halo = Math.exp(-6.0 * r * r) * 0.16;
    return sat(head + tail + halo);
  },
  /* 12 WISP — long on +v, like STREAK */ (u, v) => {
    const n = fbm(v * 1.6 + 20, u * 4.2, 4409, 4) - 0.5;
    const w = 0.34 * Math.pow(sat(1 - Math.abs(v)), 0.8);
    const d = Math.abs(u - n * 0.5) / Math.max(0.02, w);
    return sat(Math.exp(-d * d * 1.6) * Math.pow(sat(1 - Math.abs(v)), 0.9) * 0.85);
  },
  /* 13 RAYS */ (u, v, r) => {
    const a = Math.atan2(v, u);
    let s = 0;
    for (let i = 0; i < 5; i++) {
      const n = 6 + i * 5;
      const p = Math.abs(Math.cos(a * n * 0.5 + i * 1.7));
      s += Math.pow(p, 34 - i * 4) * (0.34 - i * 0.05);
    }
    const rays = s * Math.pow(sat(1 - r), 1.25);
    const core = Math.exp(-26 * r * r) * 0.85;
    return sat(rays + core) * sstep(1.0, 0.9, r);
  },
  /* 14 DEBRIS */ (u, v, r) => {
    const a = Math.atan2(v, u);
    const rad = 0.52 + 0.24 * vn(Math.cos(a) * 1.6 + 3, Math.sin(a) * 1.6 + 3, 8123);
    return sstep(rad + 0.05, rad - 0.05, r);
  },
  /* 15 SMOKE */ (u, v, r) => {
    const n = fbm(u * 1.35 + 40, v * 1.35 + 40, 60611, 5) - 0.5;
    return sat(Math.exp(-1.9 * r * r) * (0.62 + n * 0.8)) * sstep(1.02, 0.55, r);
  },
];

/**
 * Build the atlas. Returns a THREE.Texture ready to bind.
 * @param {number} cell pixels per cell (256 -> 1024px atlas)
 */
export function buildSpriteAtlas(cell = 256) {
  const N = ATLAS_GRID, size = cell * N;
  const data = new Uint8Array(size * size * 4);
  const inv = 2 / (cell - 1);
  for (let s = 0; s < N * N; s++) {
    const f = SHAPES[s] || SHAPES[0];
    const ox = (s % N) * cell, oy = ((s / N) | 0) * cell;
    for (let py = 0; py < cell; py++) {
      const v = py * inv - 1;                       // +v is up in shape space AND in GL uv
      for (let px = 0; px < cell; px++) {
        const u = px * inv - 1;
        const r = Math.sqrt(u * u + v * v);
        // 4 % inset so mip levels never bleed one cell into its neighbour
        const guard = sstep(1.0, 0.94, Math.max(Math.abs(u), Math.abs(v)));
        let a = f(u, v, r) * guard;
        a = a > 1 ? 1 : a < 0 ? 0 : a;
        const k = ((oy + py) * size + (ox + px)) * 4;
        data[k] = 255; data[k + 1] = 255; data[k + 2] = 255;
        data[k + 3] = (a * 255) | 0;
      }
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.name = 'vfxAtlas';
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.colorSpace = THREE.NoColorSpace;   // it is a mask, never colour data
  tex.needsUpdate = true;
  return tex;
}

/**
 * The ribbon gradient used by weapon trails: u runs ALONG the trail, v runs
 * ACROSS it (0 = hilt edge, 1 = blade edge). The blade edge is a hot white
 * spine and the hilt edge falls to nothing, which is what makes a DW trail
 * read as a swept blade rather than a flat strip of colour.
 */
export function buildTrailTexture(w = 128, h = 64) {
  const data = new Uint8Array(w * h * 4);
  for (let y = 0; y < h; y++) {
    const v = (y + 0.5) / h;
    // ACROSS the ribbon. A smooth 0..1 gradient reads as a translucent sheet,
    // which is the classic "browser trail" tell. What DW shows is a defined
    // band: nothing below the middle, a fast ramp, then a white spine right on
    // the blade edge and a hard cut past it.
    const band = sstep(0.40, 0.90, v) * 0.88;
    const spine = Math.exp(-((v - 0.905) ** 2) / 0.0060) * 1.25;
    const cut = sstep(1.0, 0.975, v);
    for (let x = 0; x < w; x++) {
      const u = (x + 0.5) / w;
      const grain = 0.80 + 0.20 * vn(u * 26, v * 3.0, 5501);
      const a = sat((band + spine) * cut * grain);
      const k = (y * w + x) * 4;
      data[k] = 255; data[k + 1] = 255; data[k + 2] = 255; data[k + 3] = (a * 255) | 0;
    }
  }
  const tex = new THREE.DataTexture(data, w, h, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.name = 'vfxTrail';
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearFilter;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.colorSpace = THREE.NoColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/**
 * The ground-decal atlas: 4x4 cells of scorch, scuff and boot marks, drawn
 * with a soft irregular edge so nothing reads as a circle stamped on grass.
 *
 *   0 scorch   1 scorch(soft)  2 scuff       3 scuff(long)
 *   4 bootL    5 bootR         6 crater      7 crack star
 *   8 splat    9 drag streak  10 ash ring   11 dirt patch
 *  12 ring(sharp) 13 ring(soft) 14 glow disc 15 rune disc
 */
export function buildDecalAtlas(cell = 128) {
  const N = 4, size = cell * N;
  const data = new Uint8Array(size * size * 4);
  const inv = 2 / (cell - 1);
  const boot = (u, v, flip) => {
    const uu = u * flip;
    // sole: rounded rectangle; heel: separate pad
    const sole = sstep(0.06, -0.02, Math.max(Math.abs(uu / 0.34) - 1, Math.abs((v - 0.22) / 0.52) - 1));
    const heel = sstep(0.06, -0.02, Math.max(Math.abs(uu / 0.30) - 1, Math.abs((v + 0.55) / 0.26) - 1));
    const tread = 0.55 + 0.45 * Math.abs(Math.cos(v * 26));
    return sat((sole * tread + heel) * (0.75 + 0.25 * vn(uu * 9, v * 9, 77)));
  };
  const F = [
    (u, v, r) => sat(Math.exp(-2.0 * r * r) * (0.55 + 0.75 * (fbm(u * 2.6, v * 2.6, 91, 4) - 0.3))) * sstep(1.0, 0.45, r),
    (u, v, r) => sat(Math.exp(-1.35 * r * r) * (0.5 + 0.6 * fbm(u * 1.7, v * 1.7, 191, 4))) * sstep(1.0, 0.3, r),
    (u, v, r) => sat(Math.exp(-((v * v) / 0.05)) * Math.pow(sat(1 - Math.abs(u)), 1.1) * (0.4 + 0.9 * fbm(u * 7, v * 7, 291, 3))),
    (u, v, r) => sat(Math.exp(-((v * v) / 0.018)) * Math.pow(sat(1 - Math.abs(u)), 0.7) * (0.35 + 1.0 * fbm(u * 11, v * 5, 391, 3))),
    (u, v) => boot(u, v, 1),
    (u, v) => boot(u, v, -1),
    (u, v, r) => sat((Math.exp(-((r - 0.42) ** 2) / 0.03) * 0.9 + Math.exp(-4.5 * r * r) * 0.7) * (0.5 + 0.7 * fbm(u * 3.4, v * 3.4, 591, 4))) * sstep(1.0, 0.5, r),
    (u, v, r) => {
      const a = Math.atan2(v, u); let s = 0;
      for (let i = 0; i < 7; i++) { const t = i * 0.897 + 0.4; s += Math.exp(-((((a - t + Math.PI * 3) % (Math.PI * 2)) - Math.PI) ** 2) / 0.0016) * sat(1 - r); }
      return sat(s * 0.9 + Math.exp(-30 * r * r) * 0.6);
    },
    (u, v, r) => {
      const a = Math.atan2(v, u);
      const rad = 0.36 + 0.30 * vn(Math.cos(a) * 2.2 + 7, Math.sin(a) * 2.2 + 7, 691);
      return sat(sstep(rad + 0.16, rad - 0.10, r) * (0.6 + 0.6 * fbm(u * 4, v * 4, 791, 3)));
    },
    (u, v) => sat(Math.exp(-((v * v) / 0.10)) * sstep(1.0, -0.4, u) * (0.3 + 1.0 * fbm(u * 6 + 3, v * 12, 891, 3))),
    (u, v, r) => sat(Math.exp(-((r - 0.62) ** 2) / 0.016) * (0.45 + 0.8 * fbm(u * 5, v * 5, 991, 3))) * sstep(1.0, 0.9, r),
    (u, v, r) => sat(sstep(0.95, 0.15, r + (fbm(u * 2.1, v * 2.1, 1091, 4) - 0.5) * 0.75) * 0.85),
    (u, v, r) => sat(Math.exp(-((r - 0.80) ** 2) / 0.0016)) * sstep(1.0, 0.95, r),
    (u, v, r) => sat(Math.exp(-((r - 0.74) ** 2) / 0.018) + sstep(0.74, 0.2, r) * 0.06) * sstep(1.0, 0.92, r),
    (u, v, r) => sat(Math.exp(-2.6 * r * r)) * sstep(1.0, 0.6, r),
    (u, v, r) => {
      const a = Math.atan2(v, u);
      const ring = Math.exp(-((r - 0.86) ** 2) / 0.0022) + Math.exp(-((r - 0.62) ** 2) / 0.0009) * 0.7;
      const teeth = Math.pow(Math.abs(Math.cos(a * 12)), 22) * sstep(0.86, 0.68, r) * sstep(0.5, 0.66, r);
      return sat(ring + teeth + Math.exp(-18 * r * r) * 0.35) * sstep(1.0, 0.95, r);
    },
  ];
  for (let s = 0; s < 16; s++) {
    const f = F[s], ox = (s % N) * cell, oy = ((s / N) | 0) * cell;
    for (let py = 0; py < cell; py++) {
      const v = py * inv - 1;
      for (let px = 0; px < cell; px++) {
        const u = px * inv - 1;
        const r = Math.sqrt(u * u + v * v);
        const guard = sstep(1.0, 0.92, Math.max(Math.abs(u), Math.abs(v)));
        let a = f(u, v, r) * guard;
        a = a > 1 ? 1 : a < 0 ? 0 : a;
        const k = ((oy + py) * size + (ox + px)) * 4;
        data[k] = 255; data[k + 1] = 255; data[k + 2] = 255; data[k + 3] = (a * 255) | 0;
      }
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.name = 'vfxDecals';
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.colorSpace = THREE.NoColorSpace;
  tex.needsUpdate = true;
  return tex;
}
